import {MobileProps} from '@common/model/MobilePropsModel';
import sanitizeHtml from 'sanitize-html';

/**
 * The function removes null values from an object and returns a new object without those null values.
 * @param {T} object - The `object` parameter is the input object from which you want to remove null
 * values.
 * @returns The function `removeNullValuesFromObject` returns a new object with all null or undefined
 * values removed from the input object.
 */
export const removeNullValuesFromObject = <T extends any | undefined>(object: T) => {
    return Object.fromEntries(Object.entries(object ?? {}).filter(([, v]) => v !== null && v !== undefined)) as T;
};

// Cache pro env hodnoty
const envCache = new Map<
    string,
    {
        data: any;
        timestamp: number;
        ttl: number;
    }
>();
// Cache pro probíhající requesty
const pendingRequests = new Map<string, Promise<any>>();

export const getEnvValue = async <T extends string | readonly string[]>(
    envKey: T,
    options?: {cacheTTL?: number},
): Promise<undefined | (T extends readonly string[] ? {[K in T[number]]: string | undefined} : string | undefined)> => {
    try {
        const isEnvArray = Array.isArray(envKey);
        const cacheKey = isEnvArray ? envKey.join(',') : (envKey as string);
        const now = Date.now();

        // Kontrola cache
        const cached = envCache.get(cacheKey);
        if (cached && now - cached.timestamp < (options?.cacheTTL ?? 5 * 60 * 1000)) {
            return cached.data;
        }

        // Kontrola, zda již neprobíhá stejný request
        const pendingRequest = pendingRequests.get(cacheKey);
        if (pendingRequest) {
            return pendingRequest;
        }

        // Vytvoření nového requestu
        const promise = (async () => {
            try {
                const origin =
                    typeof window !== 'undefined'
                        ? window.location.origin
                        : process.env.PORT
                          ? `http://localhost:${process.env.PORT}`
                          : 'http://localhost:8080';
                const queryParams = isEnvArray
                    ? (envKey as string[]).map((key) => `env=${encodeURIComponent(key)}`).join('&')
                    : `env=${encodeURIComponent(envKey as string)}`;

                const response = await fetch(`${origin}/api/env?${queryParams}`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const result = isEnvArray ? (data.env as T) : data.env[envKey as keyof T];

                // Uložení do cache
                envCache.set(cacheKey, {
                    data: result,
                    timestamp: now,
                    ttl: options?.cacheTTL ?? 5 * 60 * 1000,
                });

                return result;
            } finally {
                // Vyčištění pending requestu po dokončení
                pendingRequests.delete(cacheKey);
            }
        })();

        // Uložení probíhajícího requestu do cache
        pendingRequests.set(cacheKey, promise);

        return promise;
    } catch (e) {
        return undefined;
    }
};

export const environments = {
    KUBERNETES_NAMESPACE: process.env.KUBERNETES_NAMESPACE,
    SARA_CLIENT_ID: process.env.SARA_CLIENT_ID,
    SARA_OAUTH_REDIRECT_URL: process.env.SARA_OAUTH_REDIRECT_URL,
    SARA_AUTHORIZATION_ENDPOINT: process.env.SARA_AUTHORIZATION_ENDPOINT,
    FRONTEND_SELFCARE_URL: process.env.FRONTEND_SELFCARE_URL,
    FRONTEND_SOP_WEB_URL: process.env.FRONTEND_SOP_WEB_URL,
};

/**
 * The `serverLogger` function logs data with a specified name and log type, with an option to hide logs in
 * production or pre-production environments.
 * @param  - The `serverLogger` function takes in an object with the following parameters:
 * @returns If the `mode` is set to 'hide' and `isProdPreprod` is true, nothing will be logged and the
 * function will return undefined. Otherwise, the function will log the message using
 * `console[logType]` and return undefined.
 */
export const serverLogger = <T extends string, U extends Record<string, unknown> | unknown>({
    name,
    data,
    logType = 'log',
    mode = 'development',
}: {
    name: T;
    data: U;
    logType?: 'error' | 'log';
    mode?: 'development' | 'production' | 'devAndProd';
}) => {
    const isProdPreprod = process.env.KUBERNETES_NAMESPACE === 'prod' || process.env.KUBERNETES_NAMESPACE === 'preprod';
    if ((mode === 'production' && isProdPreprod) || (mode === 'development' && !isProdPreprod) || mode === 'devAndProd') {
        // eslint-disable-next-line no-console
        console[logType](`%c 🚨 -> ${name}: `, 'color: #e13019', data);
        return;
    }
    return;
};

/**
 * The function `getFromLS` retrieves data from local storage based on the specified id and optional
 * key.
 * @param {'mobile' | 'desktop'} id - The `id` parameter in the `getFromLS` function is a string
 * literal type that can only have the values `'mobile'` or `'desktop'`.
 * @param [key] - The `key` parameter in the `getFromLS` function is of type `keyof MobileProps`, which
 * means it can be any key that exists in the `MobileProps` type. It is used to specify which specific
 * property of the `MobileProps` object you want to retrieve from the
 * @returns The function `getFromLS` returns a value of type `MobileProps`. If a `key` parameter is
 * provided, it returns the value corresponding to that key from the local storage object for the
 * specified `id` ('mobile' or 'desktop'). If no `key` is provided, it returns the entire local storage
 * object for the specified `id`.
 */
export const getFromLS = (id: 'mobile' | 'desktop', key?: keyof MobileProps): MobileProps => {
    let ls = {} as Record<string, any>;
    if (global.localStorage) {
        try {
            ls = JSON.parse(global.localStorage.getItem(id) ?? '{}') || {};
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`%c 🚨 -> getFromLS error: `, 'color: #e13019', error);
        }
    }
    return key ? ls[key] : ls;
};

type RNDataInput = {
    saveUserId?: string;
    openBiometrics?: boolean;
    openNativeCamera?: boolean;
    openCameraPermission?: boolean;
    disableAutologin?: boolean;
    /**
     * @deprecated
     * @description This property is deprecated and will be removed in future versions.
     */
    logouting?: boolean;
    logout?: boolean;
    hideTabs?: 'HIDE' | 'SHOW';
    openNativeSettings?: boolean;
    openLocationPermission?: boolean;
    downloadFile?: string;
    biometricsAutoLogin?: 'TRUE' | 'FALSE';
    canRateMobileApp?: boolean | null;
    userEmail?: string | null;
    userPhone?: string | null;
};

export const sendDataToRN = (data: RNDataInput) => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({data}, null, 2));
};

/**
 * Transform path
 * @param {string} pathname
 */
export const transformPath = (pathname: string) =>
    pathname === '/' ? 'Nástěnka' : path[pathname]?.replace(/([a-zA-Za-žA-Ž ]+)[.!?]?\s*$/, '<strong>$1</strong>');

const path = {
    '/': 'Nástěnka',
    '/odbernaMista': 'Odběrná místa',
    '/spravaUctu': 'Správa účtu',
    '/_error': 'Nenalezeno',
} as {readonly [key: string]: string};

/**
 * It takes a string of HTML and returns a string of HTML that's been sanitized
 * @param {string} html - The HTML string to sanitize.
 */
export const sanitizeHTML = (html: string) => sanitizeHtml(html);

/**
 * It takes an object with string keys and string values, and returns a new object with the keys and
 * values swapped
 * @param map - {[key: string]: string} - this is the object we want to reverse
 */
export const reversObjectKeyValue = (map: {[key: string]: string}) =>
    Object.entries(map).reduce((acc, [key, value]) => ({...acc, [value]: key}), {} as {[key: string]: string});
